import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    afterRenderEffect,
    computed,
    inject,
    input,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';

import { DeviceData, DeviceSize } from '../_shared/data/device.data';
import { ImageDataDb } from '../_shared/data/image-data-db.data';
import { LinkDirective } from '../_shared/directives/link.directive';
import { IButton } from '../_shared/models/button/button.model';
import { ImageData } from '../_shared/models/image/image-data.model';
import { MediaImage } from '../_shared/models/media/media-image.model';
import { TextStyleSize } from '@sirius/typography';
import { ButtonComponent } from '../button/button.component';
import { HeadingComponent } from '../heading/heading.component';
import { ImageItemComponent } from '../media/media-item/image-item/image-item.component';
import { ContainerComponent } from '../container/container.component';

@Component({
    selector: 'sirius-hero-v3-slide',
    templateUrl: './hero-v3-slide.component.html',
    styleUrls: ['./hero-v3-slide.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, HeadingComponent, ImageItemComponent, LinkDirective, ContainerComponent],
})
export class HeroV3SlideComponent {
    readonly backgroundImage = input<MediaImage>();
    readonly backgroundImageMobile = input<MediaImage>();
    readonly backgroundVideoUrl = input<string>();
    readonly showVideoOnMobile = input(false);
    readonly eyebrow = input<string>();
    readonly title = input<string>();
    readonly subTitle = input<string>();
    readonly buttonPrimary = input<IButton>();
    readonly buttonSecondary = input<IButton>();
    readonly contentAlignment = input<'left' | 'center'>('left');
    readonly isActive = input(false);

    readonly imageData: ImageData | undefined = ImageDataDb.hero.image;

    readonly videoPosterUrl = computed<string>(() => this.backgroundImage()?.image?.url ?? '');
    readonly hasCta = computed<boolean>(() => !!(this.buttonPrimary() || this.buttonSecondary()));
    readonly hasMobileMedia = computed<boolean>(
        () => !!this.backgroundImageMobile() || (!!this.backgroundVideoUrl() && this.showVideoOnMobile())
    );

    private readonly _breakpointObserver = inject(BreakpointObserver);
    private readonly _videoRef = viewChild<ElementRef<HTMLVideoElement>>('videoElement');

    readonly isCompact = toSignal(
        this._breakpointObserver
            .observe(`(max-width: ${DeviceData.breakpoints[DeviceSize.MD] - 1}px)`)
            .pipe(map((state) => state.matches)),
        { initialValue: false }
    );
    readonly titleSize = computed<TextStyleSize>(() => (this.isCompact() ? 'm' : 'l'));

    constructor() {
        afterRenderEffect((onCleanup) => {
            const video = this._videoRef()?.nativeElement;
            if (!video) return;

            if (this.isActive()) {
                video.muted = true;
                video.currentTime = 0;
                video.play()?.catch(() => undefined);
            } else {
                video.pause();
            }

            onCleanup(() => video.pause());
        });
    }
}
